import { createSlice } from '@reduxjs/toolkit';
import { type IManager } from '../../types/IManager';
import {
  createManagerThunk,
  updateManagerThunk,
  getManagersThunk,
  getManagersSilentThunk,
  deleteManagerThunk
} from './managersAccessesThunks';

export interface IManagersAccessesState {
  managers: IManager[];
  total: number;
  isLoading: boolean;
  error: string | null;
  isCreating: boolean;
  createError: string | null;
  isUpdating: boolean;
  updateError: string | null;
  updatingManagerId: number | null;
  isDeleting: boolean;
  deleteError: string | null;
}

const initialState: IManagersAccessesState = {
  managers: [],
  total: 0,
  isLoading: false,
  error: null,
  isCreating: false,
  createError: null,
  isUpdating: false,
  updateError: null,
  updatingManagerId: null,
  isDeleting: false,
  deleteError: null
};

const managersAccessesSlice = createSlice({
  name: 'managersAccesses',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCreateError: (state) => {
      state.createError = null;
    },
    clearUpdateError: (state) => {
      state.updateError = null;
    },
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    resetManagersState: (state) => {
      state.managers = [];
      state.total = 0;
      state.isLoading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Создание менеджера
      .addCase(createManagerThunk.pending, (state) => {
        state.isCreating = true;
        state.createError = null;
      })
      .addCase(createManagerThunk.fulfilled, (state, action) => {
        state.isCreating = false;
        state.managers.push(action.payload);
        state.createError = null;
      })
      .addCase(createManagerThunk.rejected, (state, action) => {
        state.isCreating = false;
        state.createError =
          action.payload?.message ?? 'Произошла ошибка при создании менеджера';
      })
      // Обновление менеджера
      .addCase(updateManagerThunk.pending, (state, action) => {
        state.isUpdating = true;
        state.updateError = null;
        state.updatingManagerId = action.meta.arg.managerId;
      })
      .addCase(updateManagerThunk.fulfilled, (state) => {
        state.isUpdating = false;
        state.updateError = null;
      })
      .addCase(updateManagerThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.updatingManagerId = null;
        state.updateError =
          action.payload?.message ??
          'Произошла ошибка при обновлении менеджера';
      })
      // Получение списка менеджеров
      .addCase(getManagersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getManagersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.managers = action.payload.items;
        state.total = action.payload.meta.total;
        state.error = null;
      })
      .addCase(getManagersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ??
          'Произошла ошибка при получении списка менеджеров';
      })
      // Тихое получение списка менеджеров (без лоадера)
      .addCase(getManagersSilentThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(getManagersSilentThunk.fulfilled, (state, action) => {
        state.managers = action.payload.items;
        state.total = action.payload.meta.total;
        state.error = null;
        state.updatingManagerId = null;
      })
      .addCase(getManagersSilentThunk.rejected, (state, action) => {
        state.error =
          action.payload?.message ??
          'Произошла ошибка при обновлении списка менеджеров';
        state.updatingManagerId = null;
      })
      // Удаление менеджера
      .addCase(deleteManagerThunk.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
      })
      .addCase(deleteManagerThunk.fulfilled, (state) => {
        state.isDeleting = false;
        state.deleteError = null;
      })
      .addCase(deleteManagerThunk.rejected, (state, action) => {
        state.isDeleting = false;
        state.deleteError =
          action.payload?.message ?? 'Произошла ошибка при удалении менеджера';
      });
  }
});

export const {
  clearError,
  clearCreateError,
  clearUpdateError,
  clearDeleteError,
  resetManagersState
} = managersAccessesSlice.actions;

export {
  createManagerThunk,
  updateManagerThunk,
  getManagersThunk,
  getManagersSilentThunk,
  deleteManagerThunk
};

export default managersAccessesSlice.reducer;
